import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  CloseButton,
  HStack,
  Text,
  Spinner,
} from '@chakra-ui/react';

export interface EmailVerificationBannerProps {
  email: string;
  onResend?: () => Promise<void>;
  onDismiss?: () => void;
}

export function EmailVerificationBanner({
  email,
  onResend,
  onDismiss,
}: EmailVerificationBannerProps) {
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) return null;

  const handleResend = async () => {
    if (!onResend) return;
    setIsResending(true);
    setResendStatus('idle');
    try {
      await onResend();
      setResendStatus('success');
      setTimeout(() => setResendStatus('idle'), 5000);
    } catch {
      setResendStatus('error');
      setTimeout(() => setResendStatus('idle'), 5000);
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  return (
    <Alert status="warning" borderRadius="md" mb={4}>
      <AlertIcon />
      <AlertDescription flex={1} fontSize="sm">
        Please verify your email address ({email}) to access all features.
      </AlertDescription>
      <HStack spacing={2}>
        {resendStatus === 'success' ? (
          <Text color="green.500" fontSize="xs">Verification email sent!</Text>
        ) : resendStatus === 'error' ? (
          <Text color="red.500" fontSize="xs">Failed to send. Try again later.</Text>
        ) : onResend ? (
          <Button
            size="sm"
            variant="outline"
            onClick={handleResend}
            isDisabled={isResending}
            leftIcon={isResending ? <Spinner size="xs" /> : undefined}
          >
            {isResending ? 'Sending...' : 'Resend Email'}
          </Button>
        ) : null}
        <CloseButton size="sm" onClick={handleDismiss} aria-label="Dismiss" />
      </HStack>
    </Alert>
  );
}

export default EmailVerificationBanner;
